// Single auth page — "Continue with Google" primary CTA, email/password fallback below.
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ChevronRight, Mail } from "lucide-react";

import { useAuth } from "../auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

const TOS_URL = "https://startup.tryinfin.com/terms";
const PRIVACY_URL = "https://startup.tryinfin.com/privacy";

export default function AuthPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [showEmail, setShowEmail] = useState(false);
  const [mode, setMode] = useState("signin"); // signin | signup
  const [form, setForm] = useState({ display_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === "signin") {
        u = await login(form.email, form.password);
      } else {
        u = await register(form);
      }
      if (u.must_change_password) nav("/change-password");
      else if (u.is_admin) nav("/admin");
      else nav(location.state?.next || "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--canvas)] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 text-slate-900">
          <Building2 size={22} className="text-[#C5283F]" />
          <span className="font-display text-xl font-semibold">Infin Entities</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="font-display text-2xl font-semibold mb-1">Sign in to continue</h1>
          <p className="text-sm text-slate-500 mb-7">
            One account for all your incorporation cases.
          </p>

          {/* Primary CTA: Continue with Google */}
          <button
            onClick={startGoogleSignIn}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 rounded-lg py-3.5 font-medium text-slate-900 hover:bg-slate-50 hover:border-slate-400 transition-colors shadow-sm">
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />
            <span>or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {!showEmail ? (
            <button
              onClick={() => setShowEmail(true)}
              data-testid="show-email-form-btn"
              className="w-full text-sm text-slate-600 hover:text-slate-900 inline-flex items-center justify-center gap-2 py-2">
              <Mail size={14} /> Continue with email
              <ChevronRight size={14} />
            </button>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="email-form">
              <div className="flex bg-slate-100 rounded-lg p-1 text-xs font-medium">
                <button type="button" onClick={() => setMode("signin")}
                        data-testid="mode-signin-btn"
                        className={`flex-1 py-1.5 rounded-md transition-colors ${mode === "signin" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}>
                  Sign in
                </button>
                <button type="button" onClick={() => setMode("signup")}
                        data-testid="mode-signup-btn"
                        className={`flex-1 py-1.5 rounded-md transition-colors ${mode === "signup" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}>
                  Create account
                </button>
              </div>
              {mode === "signup" && (
                <Field label="Your name" value={form.display_name}
                       onChange={(v) => setForm({ ...form, display_name: v })}
                       testid="auth-name" />
              )}
              <Field label="Email" type="email" value={form.email}
                     onChange={(v) => setForm({ ...form, email: v })}
                     testid="auth-email" />
              <Field label="Password" type="password" value={form.password}
                     onChange={(v) => setForm({ ...form, password: v })}
                     testid="auth-password"
                     minLength={mode === "signup" ? 8 : undefined} />
              <button type="submit" disabled={busy}
                      data-testid="auth-submit-btn"
                      className="w-full bg-[#C5283F] text-white rounded-lg py-2.5 font-medium hover:bg-[#A82035] disabled:opacity-50 transition-colors mt-1">
                {busy ? "…" : (mode === "signin" ? "Sign in" : "Create account")}
              </button>
            </form>
          )}

          <div className="text-[11px] text-slate-400 mt-6 text-center leading-relaxed">
            By continuing you agree to our{" "}
            <a href={TOS_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-700">Terms of Service</a>
            {" "}and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-700">Privacy Policy</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, minLength }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required
        minLength={minLength}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent"
      />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  );
}
